import { supabase } from './supabase';

const SESSION_KEY = 'snapscrap_session';

function toBase64(bytes) {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function fromBase64(value) {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export async function createPasswordSalt() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return toBase64(bytes);
}

export async function hashPassword(password, salt) {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );

  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: fromBase64(salt),
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    256
  );

  return toBase64(new Uint8Array(bits));
}

function publicUser(row) {
  if (!row) return null;
  return {
    id: row.id,
    full_name: row.full_name,
    email: row.email,
    phone: row.phone,
  };
}

export function readSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.id || !parsed?.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeSession(user) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

export async function signupUser({ fullName, phone, email, password }) {
  const normalizedEmail = email.trim().toLowerCase();
  const cleanName = fullName.trim();
  const cleanPhone = phone.trim();

  if (!cleanName || !cleanPhone || !normalizedEmail || !password) {
    throw new Error('Please fill in all required fields.');
  }
  if (password.length < 6) {
    throw new Error('Password must be at least 6 characters.');
  }

  const { data: existing, error: existingError } = await supabase
    .from('app_users')
    .select('id')
    .eq('email', normalizedEmail)
    .maybeSingle();

  if (existingError) throw existingError;
  if (existing) throw new Error('An account with this email already exists. Please log in.');

  const password_salt = await createPasswordSalt();
  const password_hash = await hashPassword(password, password_salt);

  const { data, error } = await supabase
    .from('app_users')
    .insert({
      full_name: cleanName,
      phone: cleanPhone,
      email: normalizedEmail,
      password_salt,
      password_hash,
    })
    .select('id, full_name, email, phone')
    .single();

  if (error) {
    if (/duplicate|unique/i.test(error.message || '')) {
      throw new Error('An account with this email already exists. Please log in.');
    }
    throw error;
  }

  const user = publicUser(data);
  writeSession(user);
  return user;
}

export async function loginUser({ email, password }) {
  const normalizedEmail = email.trim().toLowerCase();

  if (!normalizedEmail || !password) {
    throw new Error('Please enter your email and password.');
  }

  const { data, error } = await supabase
    .from('app_users')
    .select('id, full_name, email, phone, password_salt, password_hash')
    .eq('email', normalizedEmail)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('No account found with that email.');

  const password_hash = await hashPassword(password, data.password_salt);
  if (password_hash !== data.password_hash) {
    throw new Error('Incorrect password. Please try again.');
  }

  const user = publicUser(data);
  writeSession(user);
  return user;
}

export function logoutUser() {
  clearSession();
}
